/**
 * Identidade + marca do tenant (white-label PR1) — agora vinda do backend.
 *
 * A marca-mãe **patrium** (símbolo Gema-Fita + tipografia) é FIXA; este slot
 * personaliza por cliente: nome, logo, paleta, textos legais, "powered by".
 * `TenantProvider` resolve o kit de marca no boot autenticado
 * (GET /v1/tenant/branding) e aplica o tema (rampa `--brand-*`, favicon, título).
 * Falha de rede → mantém o default Patrium/VIP (nunca quebra a UI). Fora do
 * provider (login/splash pré-auth) `useTenant()` devolve o default.
 *
 * ⚠️ O tenant vem do backend (JWT), NUNCA do Host/subdomínio (guarda-corpo).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { domainApi } from '@/services/domain-api'
import type { TenantBranding } from '@/services/contracts'
import { aplicarTema } from './tema'

export interface TenantIdentidade {
  /** Nome exibido ao lado/no lugar da marca patrium. */
  nome: string
  /**
   * `nome_exibicao` como veio da API, SEM o fallback para a marca padrão. Ausente quando a busca ainda
   * não voltou, falhou ou veio vazia. Para texto que sai do sistema (ex.: mensagem de WhatsApp), onde
   * a marca padrão não pode ser dita em nome de outro tenant.
   */
  nomeExibicaoApi?: string
  /** Logo próprio (data URI) p/ fundo claro / escuro — vazio = usa a Gema-Fita. */
  logoClaro?: string
  logoEscuro?: string
  corPrimaria: string
  corAcento: string
  exibirPoweredBy: boolean
  faviconEmoji?: string
  tituloNavegador?: string
  textoNaturezaJuridica?: string
  textoConsentimentoLgpd?: string
  contatoSuporte?: string
  /**
   * Refaz o GET /v1/tenant/branding e reaplica o tema (PR2) — chamar após
   * salvar a marca na tela self-service, pro admin ver a mudança sem F5. Fora
   * do provider é no-op.
   */
  recarregar: () => Promise<void>
}

type TenantDados = Omit<TenantIdentidade, 'recarregar'>

const DEFAULT: TenantDados = {
  nome: 'VIP Associados',
  corPrimaria: '#2038E8',
  corAcento: '#1FA463',
  exibirPoweredBy: false,
}

function daApi(b: TenantBranding): TenantDados {
  return {
    nome: b.nome_exibicao || DEFAULT.nome,
    nomeExibicaoApi: b.nome_exibicao?.trim() || undefined,
    logoClaro: b.logo_claro_data_uri || undefined,
    logoEscuro: b.logo_escuro_data_uri || undefined,
    corPrimaria: b.cor_primaria,
    corAcento: b.cor_acento,
    exibirPoweredBy: b.exibir_powered_by,
    faviconEmoji: b.favicon_emoji || undefined,
    tituloNavegador: b.titulo_navegador || undefined,
    textoNaturezaJuridica: b.texto_natureza_juridica || undefined,
    textoConsentimentoLgpd: b.texto_consentimento_lgpd || undefined,
    contatoSuporte: b.contato_suporte || undefined,
  }
}

const TenantContext = createContext<TenantIdentidade>({ ...DEFAULT, recarregar: async () => {} })

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<TenantDados>(DEFAULT)
  const montadoRef = useRef(true)
  useEffect(
    () => () => {
      montadoRef.current = false
    },
    [],
  )

  const carregar = useCallback(async () => {
    try {
      const b = await domainApi.tenantBranding()
      const id = daApi(b)
      if (!montadoRef.current) return
      setTenant(id)
      aplicarTema({ corPrimaria: id.corPrimaria, faviconEmoji: id.faviconEmoji, tituloNavegador: id.tituloNavegador })
    } catch {
      /* mantém o estado atual (default Patrium/VIP no boot) — nunca quebra a UI */
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const value = useMemo<TenantIdentidade>(() => ({ ...tenant, recarregar: carregar }), [tenant, carregar])

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

/** Hook fino — marca do tenant. Fora do provider = default Patrium/VIP. */
export function useTenant(): TenantIdentidade {
  return useContext(TenantContext)
}
